// File: frontend/src/pages/AdminInstanceProfile.js
// Version: 1.0 (2026-04-23) Phase 06.1
// Description: Creator-only admin page for viewing and editing the instance's
//   CompanyProfile (is_main=True) via the /api/v1/admin/instance-profile API.
//   Built as part of Phase 06.1 Plan 06.1-03 (frontend admin UI).
//
// Auth: isCreator() helper matches the PRIMARY AuthContext user shape found in
//   this codebase — `user.roles` is an array of objects with `.name` property
//   (verified against Navbar.js:101, KatifiWidget.js:121, UserManagement.js,
//   ReputationProfileEditor.js:178-179, AdminBrainCenter.js:40). Secondary
//   fallbacks handle string[] and single-string shapes for safety.
//
// API: apiService.getInstanceProfile() / apiService.updateInstanceProfile(data)
//   — both use the existing this.makeRequest() pattern (87 usages in file).

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Paper,
    Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const CREATOR_EMAIL = 'afitbk@gmail.com';

// PRIMARY AuthContext shape (confirmed in codebase):
//   user.roles = [{name: 'CREATOR'}, {name: 'ADMINISTRATOR'}, ...]
// Secondary fallbacks preserved for defense-in-depth:
//   - user.roles as array of strings (handled by `r?.name || r` pattern)
//   - user.role as single string (legacy pattern used in VoiceAgents.js)
//   - user.email matches CREATOR_EMAIL (backend bypass pattern from deps.py:130)
const isCreator = (user) => {
    if (!user) return false;
    if (user.email === CREATOR_EMAIL) return true;
    // PRIMARY: roles is array of {name}
    if (Array.isArray(user.roles)) {
        return user.roles.some((r) => {
            const name = (typeof r === 'string' ? r : r?.name) || '';
            return name.toUpperCase() === 'CREATOR';
        });
    }
    // FALLBACK: role is a single string
    if (typeof user.role === 'string') {
        return user.role.toUpperCase() === 'CREATOR';
    }
    return false;
};

const PRACTICE_AREAS = [
    'Estate Planning',
    'Immigration',
    'Personal Injury',
    'Family Law',
    'Criminal Defense',
    'Corporate Law',
    'Real Estate',
    'Tax Law',
    'Bankruptcy',
    'Intellectual Property',
];

const TIMEZONES = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
];

const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'ru', label: 'Russian' },
    { code: 'es', label: 'Spanish' },
    { code: 'zh', label: 'Chinese' },
    { code: 'uk', label: 'Ukrainian' },
];

// Empty skeleton used when GET returns 404 / API not yet deployed
const EMPTY_PROFILE = {
    company_name: '',
    company_url: '',
    attorney_name: '',
    tagline: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    location_name: '',
    timezone: '',
    service_areas: [],
    logo_url: '',
    favicon_url: '',
    primary_color: '',
    accent_color: '',
    primary_practice_area: '',
    secondary_practice_areas: [],
    languages_supported: [],
    social_linkedin: '',
    social_facebook: '',
    social_instagram: '',
    social_twitter: '',
    social_youtube: '',
    gmb_url: '',
};

export default function AdminInstanceProfile() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Creator guard — redirect non-Creators to /dashboard
    useEffect(() => {
        if (authLoading) return;
        if (!user || !isCreator(user)) {
            toast.error('Access denied: Creator role required.');
            navigate('/dashboard');
        }
    }, [user, authLoading, navigate]);

    const normalizeProfile = (data) => {
        const safe = { ...data };
        safe.secondary_practice_areas = safe.secondary_practice_areas || [];
        safe.languages_supported = safe.languages_supported || [];
        safe.service_areas = safe.service_areas || [];
        return safe;
    };

    const loadProfile = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiService.getInstanceProfile();
            setProfile(normalizeProfile(data));
        } catch (err) {
            const msg = err.message || 'Failed to load profile';
            // Graceful fallback: if API isn't deployed yet (404) or profile missing,
            // present an empty form so Creator can still fill in data and save.
            if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
                console.warn('[AdminInstanceProfile] No profile found — starting with empty form');
                setProfile({ ...EMPTY_PROFILE });
                toast('No profile found — fill in values and Save to create one.', { icon: 'ℹ️' });
            } else {
                setError(msg);
                toast.error(`Load failed: ${msg}`);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user && isCreator(user)) {
            loadProfile();
        }
    }, [user, loadProfile]);

    const updateField = (field) => (event) => {
        setProfile((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const updateArrayField = (field) => (event) => {
        const raw = event.target.value;
        const arr =
            typeof raw === 'string'
                ? raw.split(',').map((s) => s.trim()).filter(Boolean)
                : raw;
        setProfile((prev) => ({ ...prev, [field]: arr }));
    };

    const handleSave = async () => {
        if (!profile) return;
        setSaving(true);
        try {
            // Strip read-only/system fields from the payload; backend InstanceProfileUpdate
            // schema only accepts editable columns (see Plan 01 summary).
            const payload = { ...profile };
            delete payload.id;
            delete payload.user_id;
            delete payload.is_main;
            delete payload.created_at;
            delete payload.updated_at;

            const updated = await apiService.updateInstanceProfile(payload);
            setProfile(normalizeProfile(updated));
            toast.success('Profile saved successfully');
        } catch (err) {
            const msg = err.message || 'Save failed';
            toast.error(`Save failed: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    if (authLoading || loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !profile) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={loadProfile} sx={{ mt: 2 }} variant="outlined">
                    Retry
                </Button>
            </Container>
        );
    }

    if (!profile) return null;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Company Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Instance identity:{' '}
                    <strong>{profile.company_name || '(not set)'}</strong>
                    {profile.user_id && ` (user_id=${profile.user_id}, is_main=true)`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    These values drive /api/v1/branding, AI prompts, email templates, and voice
                    agents across the platform. Edit carefully.
                </Typography>
            </Paper>

            {/* Section 1: Identity */}
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Identity</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                value={profile.company_name || ''}
                                onChange={updateField('company_name')}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Attorney Name"
                                value={profile.attorney_name || ''}
                                onChange={updateField('attorney_name')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Tagline"
                                value={profile.tagline || ''}
                                onChange={updateField('tagline')}
                                helperText="e.g., Trusted Estate Planning in NYC"
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Section 2: Contact */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Contact</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company URL"
                                value={profile.company_url || ''}
                                onChange={updateField('company_url')}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Email"
                                value={profile.contact_email || ''}
                                onChange={updateField('contact_email')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Phone"
                                value={profile.contact_phone || ''}
                                onChange={updateField('contact_phone')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                multiline
                                minRows={2}
                                value={profile.address || ''}
                                onChange={updateField('address')}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Section 3: Location */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Location</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Location Name"
                                value={profile.location_name || ''}
                                onChange={updateField('location_name')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                    value={profile.timezone || ''}
                                    onChange={updateField('timezone')}
                                    label="Timezone"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {TIMEZONES.map((tz) => (
                                        <MenuItem key={tz} value={tz}>
                                            {tz}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Service Areas (comma-separated)"
                                value={(profile.service_areas || []).join(', ')}
                                onChange={updateArrayField('service_areas')}
                                helperText="e.g., NYC, Brooklyn, Queens"
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Section 4: Branding */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Branding</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Logo URL"
                                value={profile.logo_url || ''}
                                onChange={updateField('logo_url')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Favicon URL"
                                value={profile.favicon_url || ''}
                                onChange={updateField('favicon_url')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Primary Color"
                                value={profile.primary_color || ''}
                                onChange={updateField('primary_color')}
                                type="color"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Accent Color"
                                value={profile.accent_color || ''}
                                onChange={updateField('accent_color')}
                                type="color"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Section 5: Practice */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Practice</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Primary Practice Area</InputLabel>
                                <Select
                                    value={profile.primary_practice_area || ''}
                                    onChange={updateField('primary_practice_area')}
                                    label="Primary Practice Area"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {PRACTICE_AREAS.map((pa) => (
                                        <MenuItem key={pa} value={pa}>
                                            {pa}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Secondary Practice Areas (comma-separated)"
                                value={(profile.secondary_practice_areas || []).join(', ')}
                                onChange={updateArrayField('secondary_practice_areas')}
                                helperText="e.g., Trusts, Wills, Elder Law"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Languages Supported</InputLabel>
                                <Select
                                    multiple
                                    value={profile.languages_supported || []}
                                    onChange={(e) =>
                                        setProfile((prev) => ({
                                            ...prev,
                                            languages_supported: e.target.value,
                                        }))
                                    }
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((v) => (
                                                <Chip key={v} label={v} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {LANGUAGES.map((l) => (
                                        <MenuItem key={l.code} value={l.code}>
                                            {l.label} ({l.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Section 6: Social */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Social</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="LinkedIn"
                                value={profile.social_linkedin || ''}
                                onChange={updateField('social_linkedin')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Facebook"
                                value={profile.social_facebook || ''}
                                onChange={updateField('social_facebook')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Instagram"
                                value={profile.social_instagram || ''}
                                onChange={updateField('social_instagram')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Twitter / X"
                                value={profile.social_twitter || ''}
                                onChange={updateField('social_twitter')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="YouTube"
                                value={profile.social_youtube || ''}
                                onChange={updateField('social_youtube')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Google My Business URL"
                                value={profile.gmb_url || ''}
                                onChange={updateField('gmb_url')}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={loadProfile} disabled={saving}>
                    Reload
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </Button>
            </Box>
        </Container>
    );
}
